import React, { useState, useEffect, useCallback } from 'react'
import _ from 'lodash'
import { Input, Select, DropdownItemProps } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import { commonPropTypes, formFieldWrapper } from '../formFieldWrapper'
import { FormFieldCommonProps } from '../../../types'
import styles from '../styles.scss'

const cx = classnames.bind(styles)

interface FieldProps extends Omit<FormFieldCommonProps, 'onChange' | 'onBlur'> {
  value?: string
  splitValue?: (val: string) => [string, string]
  dropdownDefault?: string
  onChange: (value: string) => void
  onBlur: (value: string) => void
  disabled?: boolean
  label?: string
  inputLabel?: string
  dropdownOptions?: DropdownItemProps[]
  compact?: boolean
  className?: string
  [key: string]: unknown
}

interface DropdownInputFieldProps {
  fieldProps: FieldProps
  showError?: boolean
}

interface FieldState {
  dropdownValue: string
  inputValue: string
}

const splitValue = (
  fieldProps: FieldProps,
  _defaultState: FieldState
): FieldState | null => {
  const val = _.get(fieldProps, 'value')
  const splitValueFn = fieldProps.splitValue

  if (typeof val !== 'undefined' && splitValueFn) {
    const [dropdownValue, inputValue] = splitValueFn(val)
    return {
      dropdownValue: dropdownValue || fieldProps.dropdownDefault || '',
      inputValue: typeof inputValue === 'undefined' ? '' : inputValue,
    }
  }
  return null
}

const DropdownInputField: React.FC<DropdownInputFieldProps> = ({
  fieldProps,
  showError = false,
}) => {
  const [dropdownValue, setDropdownValue] = useState<string>('')
  const [inputValue, setInputValue] = useState<string>('')

  // Initialize state from props
  useEffect(() => {
    const vals = splitValue(fieldProps, { dropdownValue: '', inputValue: '' })
    if (vals) {
      setDropdownValue(vals.dropdownValue)
      setInputValue(vals.inputValue)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle value changes from props (replaces UNSAFE_componentWillReceiveProps)
  const prevValueRef = React.useRef<string | undefined>(fieldProps.value)
  useEffect(() => {
    const nextValue = fieldProps.value
    const curValue = prevValueRef.current

    if (nextValue !== curValue) {
      const vals = splitValue(fieldProps, { dropdownValue, inputValue })
      if (vals) {
        setDropdownValue(vals.dropdownValue)
        setInputValue(vals.inputValue)
      }
    }
    prevValueRef.current = nextValue
  }, [fieldProps.value, fieldProps]) // eslint-disable-line react-hooks/exhaustive-deps

  const constructValue = useCallback(
    (newDropdownValue: string, newInputValue: string) =>
      `${newDropdownValue}${newInputValue}`,
    []
  )

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newInputValue = e.target.value
      setInputValue(newInputValue)
      fieldProps.onChange(constructValue(dropdownValue, newInputValue))
    },
    [dropdownValue, fieldProps, constructValue]
  )

  const onInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const newInputValue = e.target.value
      setInputValue(newInputValue)
      fieldProps.onBlur(constructValue(dropdownValue, newInputValue))
    },
    [dropdownValue, fieldProps, constructValue]
  )

  const onDropdownChange = useCallback(
    (
      _e: React.SyntheticEvent<HTMLElement>,
      data: {
        value?: string | number | boolean | (string | number | boolean)[]
      }
    ) => {
      const newDropdownValue = data.value as string
      setDropdownValue(newDropdownValue)
      fieldProps.onChange(constructValue(newDropdownValue, inputValue))
    },
    [inputValue, fieldProps, constructValue]
  )

  const {
    disabled,
    elmId,
    label,
    placeholder,
    inputLabel,
    dropdownOptions,
    dropdownDefault: _dropdownDefault,
    splitValue: _splitValue,
    compact = false,
    className,
    onChange: _onChange,
    onBlur: _onBlur,
    value: _value,
    ...rest
  } = fieldProps

  const newInput = {
    onChange: onInputChange,
    onBlur: onInputBlur,
    value: inputValue,
  }

  const newClassName = cx(
    'dropdown-input-field',
    {
      compact,
    },
    className
  )

  return (
    <Input
      className={newClassName}
      error={showError}
      placeholder={placeholder || label}
      data-elm-id={`${elmId}_input`}
      label={inputLabel}
      actionPosition={dropdownOptions ? 'left' : undefined}
      disabled={!!disabled}
      {...rest}
      {...newInput}
    >
      {!!dropdownOptions && (
        <Select
          data-elm-id={`${elmId}_input_dropdown`}
          disabled={!!disabled}
          options={dropdownOptions}
          value={dropdownValue}
          onChange={onDropdownChange}
          className={`${cx('select')} ${compact ? 'compact' : ''}`}
        />
      )}
      <input />
    </Input>
  )
}

const DropdownInputFieldWrapped = formFieldWrapper(
  (fieldProps: FieldProps, showError: boolean) => {
    return <DropdownInputField fieldProps={fieldProps} showError={showError} />
  }
) as React.FC & { propTypes?: typeof commonPropTypes }

DropdownInputFieldWrapped.propTypes = commonPropTypes

export default DropdownInputFieldWrapped
